/**
 * interactive.ts — Catálogo dos blocos INTERATIVOS oficiais do Studio
 *
 * Aqui ficam só as propriedades editáveis (o que o usuário e as LLMs veem). O
 * desenho e o comportamento vêm do módulo único
 * `supabase/functions/_shared/studio/interactive.ts`.
 *
 * Todo comportamento é nosso: nenhum bloco aceita script, HTML livre ou
 * endereço de arquivo externo de código.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (biblioteca própria, Fases 3 e 4)
 */

import { registerBlocks } from '../blockRegistry';
import type { StudioOutput } from '../types';

/** Blocos que sabem se desenhar em qualquer forma de entrega. */
const TODAS: StudioOutput[] = ['web', 'pdf', 'email'];
/** Blocos que só fazem sentido na tela. */
const SO_WEB: StudioOutput[] = ['web'];

registerBlocks([
  // -------------------------------------------------------------- Sanfona
  {
    key: 'page.accordion',
    label: 'Perguntas em sanfona',
    family: 'content',
    icon: 'chevrons-up-down',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Perguntas frequentes' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      {
        key: 'items',
        label: 'Itens (um por linha: pergunta|resposta)',
        type: 'richtext',
        defaultValue: 'Como funciona a contratação?|Em três passos: proposta, aceite e ativação.',
        syntax: 'Uma pergunta por linha: pergunta|resposta.',
        example: 'Tem fidelidade?|Não, o plano é mensal.\nComo cancelo?|Pelo painel, em um clique.',
      },
      {
        key: 'openFirst',
        label: 'Abrir o primeiro item',
        type: 'select',
        defaultValue: 'true',
        options: [
          { value: 'true', label: 'Sim' },
          { value: 'false', label: 'Não' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------- Abas
  {
    key: 'page.tabs',
    label: 'Abas de conteúdo',
    family: 'content',
    icon: 'panels-top-left',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      {
        key: 'items',
        label: 'Abas (uma por linha: rótulo|conteúdo)',
        type: 'richtext',
        defaultValue: 'Para você|Planos individuais com atendimento 24h.\nPara empresas|Gestão de equipes e relatórios.',
        syntax: 'Uma aba por linha: rotulo|conteudo. Em PDF e e-mail as abas saem empilhadas.',
        example: 'Mensal|Sem fidelidade.\nAnual|Dois meses de desconto.',
      },
    ],
  },

  // ------------------------------------------------------------ Carrossel
  {
    key: 'page.carousel',
    label: 'Carrossel de cartões',
    family: 'content',
    icon: 'gallery-horizontal',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      {
        key: 'items',
        label: 'Cartões (um por linha: imagem|título|texto)',
        type: 'richtext',
        defaultValue: '',
        syntax: 'Um cartão por linha: imagem|titulo|texto. A imagem aceita acervo://<chave> ou https://.',
        example: 'acervo://capa.equipe|Atendimento|Time próprio, 24 horas.',
      },
    ],
  },

  // ------------------------------------------------------------- Esteira
  {
    key: 'page.marquee',
    label: 'Esteira de logos',
    family: 'content',
    icon: 'move-horizontal',
    outputs: TODAS,
    props: [
      {
        key: 'items',
        label: 'Logos (um por linha: endereço|nome)',
        type: 'richtext',
        defaultValue: '',
        syntax: 'Um logo por linha: endereco|nome. Também aceita apenas o nome, como texto.',
        example: 'acervo://logo.cliente1|Cliente 1\nacervo://logo.cliente2|Cliente 2',
      },
      {
        key: 'speed',
        label: 'Velocidade',
        type: 'select',
        defaultValue: 'normal',
        options: [
          { value: 'slow', label: 'Devagar' },
          { value: 'normal', label: 'Normal' },
          { value: 'fast', label: 'Rápida' },
        ],
      },
    ],
  },

  // ------------------------------------------------------- Antes e depois
  {
    key: 'page.compare',
    label: 'Antes e depois',
    family: 'content',
    icon: 'columns-2',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      { key: 'beforeUrl', label: 'Imagem “antes”', type: 'image' },
      { key: 'afterUrl', label: 'Imagem “depois”', type: 'image' },
      { key: 'beforeLabel', label: 'Rótulo do “antes”', type: 'text', defaultValue: 'Antes' },
      { key: 'afterLabel', label: 'Rótulo do “depois”', type: 'text', defaultValue: 'Depois' },
    ],
  },

  // ---------------------------------------------------------------- Planos
  {
    key: 'page.plans',
    label: 'Planos com chave mensal/anual',
    family: 'content',
    icon: 'badge-dollar-sign',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Escolha seu plano' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      {
        key: 'items',
        label: 'Planos (um por linha)',
        type: 'richtext',
        defaultValue: 'Essencial|R$ 99/mês|R$ 79/mês|Atendimento 24h; Rede nacional|nao|Contratar|#formulario',
        syntax:
          'Um plano por linha: nome|preco_mensal|preco_anual|recursos|destaque|botao|destino. Os recursos são separados por ponto e vírgula. Destaque aceita sim ou nao.',
        example:
          'Essencial|R$ 99/mês|R$ 79/mês|Consultas; Exames|nao|Contratar|#formulario\nCompleto|R$ 199/mês|R$ 159/mês|Tudo do Essencial; Internação|sim|Contratar|#formulario',
      },
      { key: 'monthlyLabel', label: 'Rótulo mensal', type: 'text', defaultValue: 'Mensal' },
      { key: 'annualLabel', label: 'Rótulo anual', type: 'text', defaultValue: 'Anual' },
    ],
  },

  // --------------------------------------------------------- Linha do tempo
  {
    key: 'page.timeline',
    label: 'Linha do tempo',
    family: 'content',
    icon: 'git-commit-horizontal',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      {
        key: 'items',
        label: 'Marcos (um por linha: marco|título|texto)',
        type: 'richtext',
        defaultValue: 'Dia 1|Proposta enviada|Você recebe tudo por e-mail.',
        syntax: 'Um marco por linha: marco|titulo|texto.',
        example: '2024|Fundação|Começamos com 3 clientes.\n2026|Hoje|Mais de 500 empresas atendidas.',
      },
    ],
  },

  // ---------------------------------------------------------- Indicadores
  {
    key: 'page.metrics',
    label: 'Indicadores com contagem',
    family: 'content',
    icon: 'trending-up',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      {
        key: 'items',
        label: 'Números (um por linha: valor|rótulo|sufixo)',
        type: 'richtext',
        defaultValue: '500|Clientes atendidos|+\n98|Satisfação|%',
        syntax: 'Um número por linha: valor|rotulo|sufixo. O sufixo é opcional.',
        example: '1200|Veículos monitorados|+\n24|Atendimento|h',
      },
    ],
  },

  // ------------------------------------------------------ Galeria ampliável
  {
    key: 'page.lightbox',
    label: 'Galeria com ampliação',
    family: 'content',
    icon: 'image-plus',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      {
        key: 'items',
        label: 'Imagens (uma por linha: endereço|legenda)',
        type: 'richtext',
        defaultValue: '',
        syntax: 'Uma imagem por linha: endereco|legenda.',
        example: 'acervo://capa.escritorio|Nossa sede',
      },
      {
        key: 'columns',
        label: 'Colunas',
        type: 'select',
        defaultValue: '3',
        options: [
          { value: '2', label: '2 colunas' },
          { value: '3', label: '3 colunas' },
          { value: '4', label: '4 colunas' },
        ],
      },
    ],
  },

  // ------------------------------------------------------- Botão flutuante
  {
    key: 'page.floatingCta',
    label: 'Botão flutuante',
    family: 'action',
    icon: 'mouse-pointer-click',
    outputs: SO_WEB,
    props: [
      { key: 'label', label: 'Texto do botão', type: 'text', defaultValue: 'Fale conosco' },
      { key: 'href', label: 'Destino', type: 'text', defaultValue: '#formulario' },
      {
        key: 'side',
        label: 'Lado da tela',
        type: 'select',
        defaultValue: 'right',
        options: [
          { value: 'right', label: 'Direita' },
          { value: 'left', label: 'Esquerda' },
        ],
      },
    ],
  },

  // -------------------------------------------------------- Barra de aviso
  {
    key: 'page.notice',
    label: 'Barra de aviso dispensável',
    family: 'content',
    icon: 'megaphone',
    outputs: TODAS,
    props: [
      { key: 'text', label: 'Aviso', type: 'text', defaultValue: 'Condição especial neste mês.' },
      { key: 'linkLabel', label: 'Texto do link', type: 'text' },
      { key: 'href', label: 'Destino do link', type: 'text' },
    ],
  },

  // ------------------------------------------------------------- Menu fixo
  {
    key: 'page.stickyNav',
    label: 'Menu fixo com destaque',
    family: 'layout',
    icon: 'navigation',
    outputs: SO_WEB,
    props: [
      { key: 'brand', label: 'Nome da marca', type: 'text', defaultValue: 'Sua marca' },
      { key: 'logoUrl', label: 'Logo', type: 'image' },
      {
        key: 'items',
        label: 'Links (um por linha: rótulo|destino)',
        type: 'richtext',
        defaultValue: 'Planos|#planos\nPerguntas|#perguntas',
        syntax: 'Um link por linha: rotulo|destino. Use #id para rolar até uma seção da própria página.',
        example: 'Planos|#planos\nContato|#formulario',
      },
      { key: 'ctaLabel', label: 'Texto do botão', type: 'text', defaultValue: 'Quero uma proposta' },
      { key: 'ctaHref', label: 'Destino do botão', type: 'text', defaultValue: '#formulario' },
    ],
  },
]);
